import { motion, useReducedMotion } from "motion/react";
import { useQuery } from "convex/react";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { AppIcon } from "../apps/AppIcon";
import { appCategoryDisplayLabel, localizedCategoryLabel } from "../../lib/apps/categoryIcons";
import { resolveCanonicalCategoryKey } from "../../lib/apps/categoryNormalization";
import { AppFriendlyName } from "../../lib/apps/AppFriendlyName";
import { useFriendlyAppName } from "../../lib/apps/useFriendlyAppName";
import { formatDuration } from "../../lib/format";
import { ActivationCountText } from "./ActivationCountText";
import { queryRef } from "../../lib/convex/refs";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { useMarketingDemoOverlay } from "../../lib/marketing/MarketingDemoProvider";
import { FocusSeriesChart, type FocusSeriesPoint } from "./FocusSeriesChart";
import type { RingMetric } from "./Screen0Ring";

type PeriodMode = "day" | "week" | "month";

type FocusSeriesResult = {
  mode: PeriodMode;
  points: FocusSeriesPoint[];
  hasOpens: boolean;
};

const focusSeriesRef = queryRef<
  {
    anonymousDeviceId: string;
    appIdentifier?: string;
    displayName: string;
    date: string;
    mode: PeriodMode;
    isCategory?: boolean;
  },
  FocusSeriesResult
>("usage:getAppFocusSeries");

type Props = {
  displayName: string;
  appIdentifier?: string;
  category?: string;
  color: string;
  durationSeconds: number;
  totalSeconds: number;
  selectedDate: string;
  mode: PeriodMode;
  metric?: RingMetric;
  onMetricSelect?: (metric: RingMetric) => void;
  onClose: () => void;
  opens?: number;
  totalOpens?: number;
  isCategory?: boolean;
  averageMode?: boolean;
};

export function SegmentFocusCard({
  displayName,
  appIdentifier,
  category,
  color,
  durationSeconds,
  totalSeconds,
  selectedDate,
  mode,
  metric = "time",
  onMetricSelect,
  onClose,
  opens,
  totalOpens,
  isCategory = false,
  averageMode = false,
}: Props) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const anonymousDeviceId = useDeviceId();
  const appStoreName = useFriendlyAppName({ displayName, appIdentifier, category });
  const categoryLabel = isCategory
    ? localizedCategoryLabel(resolveCanonicalCategoryKey(category ?? displayName), t)
    : null;
  const resolvedName = categoryLabel ?? appStoreName;
  const hasOpens = typeof opens === "number";
  const timeShare = totalSeconds > 0 ? Math.round((durationSeconds / totalSeconds) * 100) : 0;
  const opensShare = hasOpens && totalOpens && totalOpens > 0 ? Math.round((opens / totalOpens) * 100) : null;

  const seriesReal = useQuery(focusSeriesRef, {
    anonymousDeviceId,
    appIdentifier: isCategory ? undefined : appIdentifier,
    displayName,
    date: selectedDate,
    mode,
    isCategory: isCategory || undefined,
  });
  const series = useMarketingDemoOverlay(
    "usage:getAppFocusSeries",
    {
      anonymousDeviceId,
      appIdentifier: isCategory ? undefined : appIdentifier,
      displayName,
      date: selectedDate,
      mode,
      isCategory: isCategory || undefined,
    },
    seriesReal,
  );

  const variants = prefersReducedMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.12 } },
        exit: { opacity: 0, transition: { duration: 0.08 } },
      }
    : {
        hidden: { opacity: 0, y: 8, filter: "blur(4px)" },
        visible: {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          transition: { type: "spring" as const, duration: 0.35, bounce: 0 },
        },
        exit: {
          opacity: 0,
          y: 4,
          filter: "blur(2px)",
          transition: { duration: 0.18, ease: [0.7, 0, 0.84, 0] as [number, number, number, number] },
        },
      };

  return (
    <motion.div
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={(e) => e.stopPropagation()}
      data-today-focus-card=""
      role="dialog"
      aria-label={formatTemplate(t.ring.usageDetailAria, { name: resolvedName })}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      style={{
        borderRadius: 12,
        border: "1px solid oklch(34% 0.028 165)",
        background: "color-mix(in oklch, oklch(20% 0.022 165) 90%, black)",
        boxShadow: "0 8px 28px oklch(0% 0 0 / 0.38), inset 0 1px 0 oklch(100% 0 0 / 0.05)",
        overflow: "hidden",
      }}
    >
      <div
        className="flex items-center gap-2.5 px-4"
        style={{
          minHeight: 48,
          borderBottom: "1px solid oklch(30% 0.025 165)",
        }}
      >
        <AppIcon
          displayName={displayName}
          appIdentifier={appIdentifier}
          category={category}
          color={color}
          size={10}
          isCategory={isCategory}
        />
        <div className="min-w-0">
          <span className="block truncate text-sm font-semibold text-[oklch(90%_0.018_150)]">
            {isCategory ? (
              categoryLabel
            ) : (
              <AppFriendlyName displayName={displayName} appIdentifier={appIdentifier} category={category} />
            )}
          </span>
          {!isCategory && appIdentifier ? (
            <span className="block truncate text-xs text-[oklch(72%_0.025_155)]">
              {appCategoryDisplayLabel(category, t, appIdentifier)}
            </span>
          ) : null}
        </div>
      </div>

      <div
        className="grid grid-cols-2"
        style={{ borderBottom: "1px solid oklch(30% 0.025 165)" }}
      >
        <MetricBox
          label={t.ring.screenTime}
          value={formatDuration(averageMode ? Math.round(durationSeconds) : durationSeconds, languageCode)}
          sub={formatTemplate(t.ring.percentOfTotal, { percent: timeShare })}
          emphasized={metric === "time"}
          color={color}
          borderRight
          averageMode={averageMode}
          onSelect={onMetricSelect ? () => onMetricSelect("time") : undefined}
        />
        <MetricBox
          label={t.ring.activations}
          value={
            hasOpens ? (
              <ActivationCountText
                count={opens}
                variant="metric-box"
                displayMode={averageMode ? "averaged" : "integer"}
                averageMode={averageMode}
              />
            ) : (
              t.common.emDash
            )
          }
          sub={opensShare !== null ? formatTemplate(t.ring.percentOfTotal, { percent: opensShare }) : t.ring.notTracked}
          emphasized={metric === "opens" && hasOpens}
          color={color}
          interactive={hasOpens}
          averageMode={averageMode}
          onSelect={onMetricSelect && hasOpens ? () => onMetricSelect("opens") : undefined}
        />
      </div>

      <div className="px-3 pt-3 pb-2">
        {series === undefined ? (
          <div
            className="w-full animate-pulse rounded-md"
            style={{ height: 150, background: "oklch(24% 0.02 165)" }}
            aria-hidden
          />
        ) : (
          <>
            <FocusSeriesChart
              key={metric}
              points={series.points}
              mode={mode}
              metric={metric}
              color={color}
              hasOpens={series.hasOpens}
            />
            {!series.hasOpens ? (
              <p className="mt-1 px-1 text-[11px] leading-4" style={{ color: "oklch(52% 0.016 155)" }}>
                {mode === "month"
                  ? t.ring.activationsNotTrackedMonth
                  : mode === "week"
                    ? t.ring.activationsNotTrackedWeek
                    : t.ring.activationsNotTrackedDay}
              </p>
            ) : null}
          </>
        )}
      </div>
    </motion.div>
  );
}

function MetricBox({
  label,
  value,
  sub,
  emphasized,
  color,
  borderRight = false,
  interactive = true,
  averageMode = false,
  onSelect,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
  emphasized: boolean;
  color: string;
  borderRight?: boolean;
  interactive?: boolean;
  averageMode?: boolean;
  onSelect?: () => void;
}) {
  const content = (
    <>
      <div className="mb-0.5 flex items-center gap-1.5">
        <span
          className="inline-block size-1.5 rounded-full"
          style={{ background: color, opacity: emphasized ? 1 : 0.32 }}
          aria-hidden
        />
        <p className="text-[11px] text-[oklch(58%_0.018_155)]">{label}</p>
      </div>
      {typeof value === "string" ? (
        <p
          className={`mono text-[22px] font-semibold leading-tight${
            averageMode ? " today-data-value-primary" : " text-[oklch(90%_0.018_150)]"
          }`}
        >
          {value}
        </p>
      ) : (
        <div className="leading-tight">{value}</div>
      )}
      <p className="mt-0.5 text-[11px] text-[oklch(58%_0.018_155)]">{sub}</p>
    </>
  );

  const style = borderRight ? { borderRight: "1px solid oklch(30% 0.025 165)" } : undefined;

  if (onSelect && interactive) {
    return (
      <button
        type="button"
        className="px-4 py-3 text-left transition-opacity active:opacity-70"
        style={style}
        onClick={(event) => {
          event.stopPropagation();
          onSelect();
        }}
        aria-pressed={emphasized}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="px-4 py-3" style={style}>
      {content}
    </div>
  );
}
